import { useTranslation } from 'next-i18next'
import React from 'react'
import { ILocation, LogisticsService } from 'src/types/graphql'

type IProps = {
  location: ILocation
}

// Literal i18n-key map — NOT a dynamic template-literal t() key (the typed
// `t()` resources check requires literal keys; `LogisticsService` member
// names don't derive their translation key by a consistent suffix rule).
// Mirrors the wizard's `SERVICE_LABEL_KEY` in
// src/components/AddPostFlow/steps/PropertyType.tsx — duplicated here
// because the `details` namespace and `host_listing` namespace don't share
// keys. Unrelated to `ITeam.logisticsServices` /
// src/constants/logisticsServices.ts (a pre-existing, different feature —
// free-text Soluciones Logísticas operator-matching slugs on Team).
const SERVICE_LABEL_KEY = {
  [LogisticsService.Receiving]: 'LOGISTICS_SERVICES.RECEIVING',
  [LogisticsService.PickingPacking]: 'LOGISTICS_SERVICES.PICKING_PACKING',
  [LogisticsService.Shipping]: 'LOGISTICS_SERVICES.SHIPPING',
  [LogisticsService.InventoryManagement]:
    'LOGISTICS_SERVICES.INVENTORY_MANAGEMENT'
} as const

/**
 * Host-declared 3PL services (`ILocation.logisticsServices`) — renter-facing,
 * display-only metadata. Renders only when the host declared at least one
 * service; there is no per-deal-type variation, so — mirroring `Reviews.tsx`
 * and `LocationFeatures.tsx`, the two other purely data-driven sections in
 * this tree — this reads directly off `location` rather than through
 * `useVariantSlice`. The listing-variant system exists for deal-type-driven
 * rendering decisions (see docs/claude/listing-variant-config.md); whether a
 * host listed logistics services isn't one of those.
 *
 * Chips are display-only metadata (`<ul>`/`<li>` + styled `<span>`, not
 * `<button>`) — there is no click behavior here, unlike the wizard's
 * multi-select chips this copy was duplicated from.
 */
function HostLogisticsServices({ location }: IProps) {
  const { t } = useTranslation('details')
  const { logisticsServices } = location

  if (!logisticsServices || logisticsServices.length === 0) return null

  return (
    <section className="border-t border-ink-100/15 py-12 md:py-24">
      <h2 className="max-w-md font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
        {t('LOGISTICS_SERVICES.TITLE')}
      </h2>
      <p className="mt-3 max-w-md text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
        {t('LOGISTICS_SERVICES.HELPER')}
      </p>

      <ul className="mt-8 flex flex-wrap gap-2">
        {logisticsServices.map((service) => (
          <li key={service}>
            <span className="inline-flex items-center rounded-full border border-ink-100/30 bg-cream-300 px-4 py-2 text-[13px] font-medium text-ink-700">
              {t(SERVICE_LABEL_KEY[service])}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default React.memo(HostLogisticsServices)
